import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

export const HelperWaiverCreateModal = ({ open, onClose, onCreated, user, language }) => {
  const [helperName, setHelperName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [contractText, setContractText] = useState(null);
  const [hasSig, setHasSig] = useState(false);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (open && !contractText) {
      axios.get(`${API}/helper-waivers/contract-text`, { headers: authHeaders() })
        .then(({ data }) => setContractText(data))
        .catch(() => toast.error('Erro ao carregar texto do contrato'));
    }
  }, [open, contractText]);

  const clearSig = () => {
    if (sigRef.current) sigRef.current.clear();
    setHasSig(false);
  };

  const handleClose = () => {
    clearSig();
    setHelperName('');
    setDate(new Date().toISOString().split('T')[0]);
    onClose();
  };

  const save = async () => {
    if (!helperName.trim()) { toast.error('Preencha o nome do helper'); return; }
    if (!hasSig || !sigRef.current || sigRef.current.isEmpty()) { toast.error('Assinatura obrigatória'); return; }
    try {
      setSaving(true);
      const signature = sigRef.current.getCanvas().toDataURL('image/png');
      await axios.post(`${API}/helper-waivers`, { helper_name: helperName.trim(), date, signature }, { headers: authHeaders() });
      toast.success('✍️ Termo assinado e salvo!');
      handleClose();
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar termo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[95vh] overflow-y-auto mx-2 sm:mx-4">
        <DialogHeader className="sticky top-0 bg-white z-10 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            🛡️ Novo Termo de Isenção de Seguro (Helper)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Nome Completo do Helper *</Label>
              <Input
                value={helperName}
                onChange={(e) => setHelperName(e.target.value)}
                placeholder="Ex: João Silva Santos"
                className="text-sm"
                data-testid="waiver-helper-name-input"
              />
            </div>
            <div>
              <Label className="text-sm">Data *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="text-sm" data-testid="waiver-date-input" />
            </div>
          </div>

          {/* Contrato completo — o helper deve ler antes de assinar */}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-2">
            <p className="text-xs text-amber-800 font-medium">📖 Leia o contrato abaixo com atenção antes de assinar</p>
          </div>
          <div
            className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white max-h-72 overflow-y-auto text-[11px] sm:text-xs leading-relaxed"
            data-testid="waiver-contract-text"
          >
            {!contractText ? (
              <p className="text-center text-gray-500 py-6">🔄 Carregando contrato...</p>
            ) : (
              <>
                <p className="text-center font-bold text-sm">{contractText.title}</p>
                {contractText.subtitles.map((s, i) => (
                  <p key={i} className="text-center font-semibold">{s}</p>
                ))}
                <div className="mt-3">
                  <p className="font-bold">IDENTIFICAÇÃO DAS PARTES</p>
                  <p className="mt-1"><span className="font-semibold">SUBCONTRATADO:</span> {user?.full_name || user?.username}</p>
                  <p><span className="font-semibold">HELPER:</span> {helperName || '________________________'}</p>
                </div>
                {contractText.clauses.map((c, i) => (
                  <div key={i} className="mt-3">
                    <p className="font-bold">{c.title}</p>
                    {c.items.map((item, j) => (
                      <p key={j} className="mt-1 text-justify">{item}</p>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Assinatura */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
            <Label className="text-sm font-semibold">✍️ Assinatura do Helper *</Label>
            <p className="text-xs text-purple-600 mb-3">Assine com o dedo (touch) ou mouse no campo abaixo</p>
            <div className="bg-white border-2 border-purple-300 rounded-lg overflow-hidden shadow-sm" data-testid="waiver-signature-canvas">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{
                  width: window.innerWidth < 640 ? Math.min(window.innerWidth - 100, 400) : 550,
                  height: window.innerWidth < 640 ? 150 : 180,
                  className: 'signature-canvas w-full h-auto'
                }}
                backgroundColor="white"
                penColor="#2563eb"
                dotSize={2}
                onEnd={() => setHasSig(true)}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-3">
              <Button type="button" variant="outline" size="sm" onClick={clearSig} className="bg-red-50 hover:bg-red-100 w-full sm:w-auto text-xs" data-testid="waiver-clear-signature-btn">
                🧹 Limpar Assinatura
              </Button>
              <div className="text-xs text-center sm:text-right">
                {hasSig ? <span className="text-green-600 font-medium">✅ Assinatura OK</span> : <span className="text-purple-600">💡 Assine de forma clara</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t bg-white sticky bottom-0">
          <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto text-sm" data-testid="waiver-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={!helperName.trim() || !date || !hasSig || saving}
            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto text-sm"
            data-testid="waiver-save-btn"
          >
            {saving ? '⏳ Salvando...' : '✍️ Assinar e Salvar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelperWaiverCreateModal;
